import { useMemo, useState } from "react";

import { InfoTip } from "@/components/InfoTip";
import { formatDate, formatDateTime } from "@/lib/format";
import { TIPS } from "@/lib/glossary";
import type { FdepDocument } from "@/types/api";

const PAGE_SIZE = 25;
const ALL = "__all__";

interface Props {
  documents: FdepDocument[];
  fetchedAt: string;
  stale: boolean;
  loading: boolean;
  refreshing: boolean;
  canRefresh: boolean;
  onRefresh: () => void;
  refreshError?: boolean;
}

export function DocumentsCard({
  documents,
  fetchedAt,
  stale,
  loading,
  refreshing,
  canRefresh,
  onRefresh,
  refreshError = false,
}: Props): JSX.Element {
  const [filter, setFilter] = useState<string>(ALL);
  const [page, setPage] = useState(1);

  const types = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of documents) {
      counts.set(d.document_type, (counts.get(d.document_type) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [documents]);

  const filtered = useMemo(() => {
    return filter === ALL
      ? documents
      : documents.filter((d) => d.document_type === filter);
  }, [documents, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const slice = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function changeFilter(next: string): void {
    setFilter(next);
    setPage(1);
  }

  return (
    <section className="card overflow-x-auto">
      <header className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="card-title mb-0 inline-flex items-center">
          FDEP documents ({documents.length})
          <InfoTip {...TIPS.fdepDocs} />
        </h2>
        <div className="flex items-center gap-2 text-xs text-ink-300">
          {stale && (
            <span className="inline-flex items-center">
              <span className="pill-medium">stale</span>
              <InfoTip {...TIPS.staleData} />
            </span>
          )}
          <span>fetched {formatDateTime(fetchedAt)}</span>
          {refreshError && (
            <span className="text-xs text-severity-high" role="alert">
              Refresh failed — showing last known documents.
            </span>
          )}
          {canRefresh && (
            <button
              type="button"
              className="btn-primary py-1 px-2 text-xs"
              disabled={refreshing}
              onClick={onRefresh}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          )}
        </div>
      </header>

      {loading ? (
        <div className="skeleton h-32" aria-label="Loading documents" />
      ) : documents.length === 0 ? (
        <p className="text-sm text-ink-300">No documents on record.</p>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-2 text-sm">
            <label className="text-ink-300 inline-flex items-center">Type:<InfoTip {...TIPS.docType} /></label>
            <select
              className="input max-w-md"
              value={filter}
              onChange={(e) => changeFilter(e.target.value)}
            >
              <option value={ALL}>All types ({documents.length})</option>
              {types.map(([t, n]) => (
                <option key={t} value={t}>
                  {t} ({n})
                </option>
              ))}
            </select>
          </div>

          <table className="w-full text-sm">
            <thead className="text-left text-ink-300 border-b border-ink-700">
              <tr>
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 pr-4 font-medium">Type<InfoTip {...TIPS.docType} /></th>
                <th className="py-2 pr-4 font-medium">Subject<InfoTip {...TIPS.docSubject} /></th>
                <th className="py-2 pr-4 font-medium">Permit<InfoTip {...TIPS.docPermit} /></th>
                <th className="py-2 pr-4 font-medium">File<InfoTip {...TIPS.docFile} /></th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-700">
              {slice.map((d) => (
                <tr key={d.file_path}>
                  <td className="py-2 pr-4 text-ink-300 whitespace-nowrap">
                    {formatDate(d.document_date)}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">{d.document_type}</td>
                  <td className="py-2 pr-4">{d.subject || "—"}</td>
                  <td className="py-2 pr-4 font-mono text-xs text-ink-300">
                    {d.permit_number ?? "—"}
                  </td>
                  <td className="py-2 pr-4 text-xs text-ink-300 whitespace-nowrap">
                    {d.file_type} {d.file_size}
                  </td>
                  <td className="py-2">
                    <a
                      href={d.file_path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary py-1 px-2 text-xs"
                    >
                      Open
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-between text-xs text-ink-300">
              <span>
                Page {safePage} of {totalPages} · {filtered.length} matches
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-primary py-1 px-2 text-xs"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="btn-primary py-1 px-2 text-xs"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
